// @givmo/app-admin — Approvals (`/approvals`), the full match-approval queue.
//
// The single most important recurring action on the portal, given the permanent, findable home it did
// not have (EP-DES1 §1/§2): it used to be the bottom section of a dashboard that is growing four more
// blocks. The dashboard keeps a Needs-attention SUMMARY of what is pending; this page is the work
// surface — search, status/shape filters, decided history, pagination, and the grouped request's
// disclosure.
//
// THE LANDING VIEW IS THE WHOLE HISTORY, PENDING FIRST — not a pending-only slice. Two reasons, and
// the first is the load-bearing one:
//   1. "Did I approve that?" is the question a decided-history view exists to answer, and an admin who
//      just approved a row must still SEE the row they approved settle into Approved. A view that
//      deletes a row the moment it resolves gives no confirmation that the decision landed.
//   2. It is the order the backend already returns (`donation_match_repository` sorts pending first),
//      so the default view adds no client re-sorting to disagree with the server about.
// The filters narrow from there; none of them is on by default.
//
// ★ THE MONEY PATH IS NOT OWNED HERE. `useMatchApprovals` owns the reads, the Owner gate, the decline
// mutation, and it renders <ApproveMatchDialog> + <ConfirmDialog>; the charge-safety guards live inside
// the dialog. This page owns which rows are on screen. The dashboard summary consumes the SAME hook, so
// the two surfaces cannot drift apart the way Gift Cards drifted from Give (EP-FIX3).

import { useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  FilterBar,
  FilterChip,
  Icon,
  PageHeader,
  SearchInput,
  testIdProp,
} from '@givmo/design-system';
import { SearchX } from 'lucide-react';
import type { ApprovalStatus } from '../dashboard/dashboard-decode';
import { ApprovalsTable } from './approvals-table';
import { useMatchApprovals } from './use-match-approvals';
import {
  APPROVALS_PAGE_SIZE,
  EMPTY_APPROVAL_FILTERS,
  clampPage,
  filterApprovalRows,
  hasActiveFilters,
  pageCount,
  pageRows,
  rangeLabel,
  visibleStatusFilters,
  type ApprovalFilters,
  type ApprovalKindFilter,
} from './approvals-decode';

const KIND_FILTERS: readonly { value: ApprovalKindFilter; label: string }[] = [
  { value: 'single', label: 'Single charity' },
  { value: 'grouped', label: 'Grouped' },
];

/** Toggle a value in a filter facet's selection (selecting nothing means "all", never "none"). */
function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

export function ApprovalsPage() {
  const approvals = useMatchApprovals();

  const [filters, setFilters] = useState<ApprovalFilters>(EMPTY_APPROVAL_FILTERS);
  const [page, setPage] = useState(1);

  // Any filter change resets to page 1 — staying on page 3 of a result set that just shrank to one
  // page renders an empty table over rows that exist.
  const update = (next: Partial<ApprovalFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const filtered = useMemo(
    () => filterApprovalRows(approvals.rows, filters),
    [approvals.rows, filters],
  );
  const filtering = hasActiveFilters(filters);
  const total = filtered.length;
  const totalPages = pageCount(total);
  const currentPage = clampPage(page, total);
  const visible = useMemo(() => pageRows(filtered, currentPage), [filtered, currentPage]);

  // Per-status counts label the chips off the UNFILTERED row set, so a chip's number does not change
  // as other facets are toggled (a count that moves when you filter tells you nothing).
  const statusCounts = useMemo(() => {
    const counts = new Map<ApprovalStatus, number>();
    for (const row of approvals.rows) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
    return counts;
  }, [approvals.rows]);

  return (
    <div
      className="mx-auto flex w-full max-w-[1280px] flex-col gap-20 px-24 py-24"
      {...testIdProp('page-approvals')}
    >
      <PageHeader
        title="Approvals"
        subtitle="Match requests from your employees — review what's waiting, and look up what you already decided."
      />

      <ApprovalsTable
        rows={visible}
        canManage={approvals.canManage}
        rejectingId={approvals.rejectingId}
        onApprove={approvals.openApprove}
        onDecline={approvals.openDecline}
        pendingCount={approvals.pendingCount}
        canApproveAll={approvals.canApproveAll}
        approvalTotal={approvals.approvalTotal}
        onApproveAll={approvals.openApproveAll}
        loading={approvals.loading}
        isError={approvals.isError}
        error={approvals.error}
        onRetry={approvals.refetch}
        truncated={approvals.truncated}
        listLoadedAt={approvals.listLoadedAt}
        controls={
          <>
            <SearchInput
              aria-label="Search approvals by employee or charity"
              placeholder="Employee or charity…"
              containerClassName="w-[240px]"
              value={filters.search}
              onChange={(e) => update({ search: e.target.value })}
              onClear={() => update({ search: '' })}
              testId="approvals-search"
            />
            <FilterBar ariaLabel="Filter approvals" testId="approvals-filters">
              {visibleStatusFilters(statusCounts, filters.statuses).map((status) => (
                <FilterChip
                  key={status}
                  label={status}
                  count={statusCounts.get(status) ?? 0}
                  selected={filters.statuses.includes(status)}
                  onToggle={() => update({ statuses: toggle(filters.statuses, status) })}
                  testId={`approvals-filter-status-${status.toLowerCase()}`}
                />
              ))}
              {KIND_FILTERS.map(({ value, label }) => (
                <FilterChip
                  key={value}
                  label={label}
                  selected={filters.kinds.includes(value)}
                  onToggle={() => update({ kinds: toggle(filters.kinds, value) })}
                  testId={`approvals-filter-kind-${value}`}
                />
              ))}
            </FilterBar>
          </>
        }
        emptyState={
          filtering ? (
            // A filtered-to-nothing queue is NOT "no pending approvals" — saying so would tell the
            // admin their queue is clear when it is only hidden. Distinct copy + a way back out.
            <EmptyState
              icon={<Icon icon={SearchX} size={20} aria-hidden />}
              title="No requests match these filters"
              description="Try a different search, or clear the filters to see the whole queue."
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => update(EMPTY_APPROVAL_FILTERS)}
                  testId="approvals-clear-filters"
                >
                  Clear filters
                </Button>
              }
            />
          ) : undefined
        }
        footer={
          total > 0 ? (
            <>
              <span
                className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums"
                {...testIdProp('approvals-range')}
              >
                {rangeLabel({
                  total,
                  page: currentPage,
                  truncated: approvals.truncated,
                  filtered: filtering,
                })}
              </span>
              {total > APPROVALS_PAGE_SIZE && (
                <div className="flex items-center gap-6">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                    testId="approvals-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    testId="approvals-next"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : undefined
        }
      />

      {approvals.dialogs}
    </div>
  );
}
